/**
 * Retours fournisseur — les appels (V1.6-10A).
 *
 * Trois règles, les mêmes qu'en V1.6-09 et pour les mêmes raisons :
 *
 * 1. **aucun corps de requête ne porte d'argent.** Un retour V1.6-10A est
 *    physique : de la marchandise repart. Crédit, facture et remboursement
 *    n'existent nulle part ici, et prétendre les envoyer produirait des
 *    chiffres que personne ne pourrait rapprocher ;
 * 2. **l'écran ne décide d'aucun droit.** `can_edit` et `can_finalize` viennent
 *    du serveur ; un bouton grisé est une politesse, pas une autorisation ;
 * 3. **l'écran ne calcule jamais ce qui est renvoyable.** Le serveur donne
 *    `returnable` — reçu moins déjà renvoyé — et refuse le reste sous verrou au
 *    moment de finaliser, quand il connaît aussi le stock et les réservations.
 *
 * `staleTime: 0`, comme les commandes : ce qui change un retour se passe
 * ailleurs — un collègue finalise, un colis part — et une fiche servie depuis
 * un cache tiède ferait ressortir du stock déjà sorti.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { orderKeys } from "./purchase-orders";
import type {
  SupplierReturnCreateInput,
  SupplierReturnDetail,
  SupplierReturnLineInput,
  SupplierReturnPage,
  SupplierReturnStatus,
  Uuid,
} from "./types";

export const supplierReturnKeys = {
  all: ["supplier-returns"] as const,
  list: (params: object) => ["supplier-returns", "list", params] as const,
  one: (returnId: Uuid) => ["supplier-returns", returnId] as const,
};

export interface SupplierReturnParams {
  [key: string]: unknown;
  supplier_id?: Uuid;
  purchase_order_id?: Uuid;
  status?: SupplierReturnStatus;
  limit?: number;
  offset?: number;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function useSupplierReturns(params: SupplierReturnParams = {}) {
  return useQuery({
    queryKey: supplierReturnKeys.list(params),
    queryFn: () => apiFetch<SupplierReturnPage>(query("/supplier-returns", params)),
    staleTime: 0,
  });
}

export function useSupplierReturn(returnId: Uuid | undefined) {
  return useQuery({
    queryKey: supplierReturnKeys.one(returnId ?? ""),
    queryFn: () => apiFetch<SupplierReturnDetail>(`/supplier-returns/${returnId}`),
    enabled: Boolean(returnId),
    staleTime: 0,
  });
}

function invalidateReturn(
  queryClient: ReturnType<typeof useQueryClient>,
  detail: SupplierReturnDetail,
) {
  queryClient.setQueryData(supplierReturnKeys.one(detail.supplier_return.id), detail);
  void queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
  const orderId = detail.supplier_return.purchase_order_id;
  if (orderId) {
    void queryClient.invalidateQueries({ queryKey: orderKeys.one(orderId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.returnable(orderId) });
  }
}

/** Préparer un retour. **Rien ne sort du stock.** */
export function useCreateSupplierReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SupplierReturnCreateInput) =>
      apiFetch<SupplierReturnDetail>("/supplier-returns", { method: "POST", body }),
    onSuccess: (data) => invalidateReturn(queryClient, data),
  });
}

export function useUpdateSupplierReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      returnId,
      lines,
    }: {
      returnId: Uuid;
      lines: SupplierReturnLineInput[];
    }) =>
      apiFetch<SupplierReturnDetail>(`/supplier-returns/${returnId}`, {
        method: "PATCH",
        body: { lines },
      }),
    onSuccess: (data) => invalidateReturn(queryClient, data),
  });
}

/**
 * Finaliser : **le seul chemin vers le grand livre.**
 *
 * Tout ou rien. Rappelée sur un retour déjà parti, elle le rend tel quel — la
 * clé d'idempotence des mouvements est dérivée du retour et de la ligne, donc
 * rien ne peut sortir deux fois.
 */
export function useFinalizeSupplierReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ returnId }: { returnId: Uuid }) =>
      apiFetch<SupplierReturnDetail>(`/supplier-returns/${returnId}/finalize`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      invalidateReturn(queryClient, data);
      // Une finalisation a fait sortir du stock : les écrans d'inventaire
      // mentent jusqu'à ce qu'ils relisent.
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
